import type { Disclosure, RoundAggregate } from "./aggregate";

const INVESTIGATION_CONFIRMED_BIT = 8;

export type InvestigationState = {
  interest: number | undefined;
  confirmedReviewer: boolean;
};

type ProposalMemberIndex = {
  proposalIndex: number;
  memberIndex: number;
};

export function decodeInvestigation(code: number): InvestigationState {
  const interestCode = code & 0b111;
  return {
    interest: interestCode === 0 ? undefined : interestCode - 1,
    confirmedReviewer: (code & INVESTIGATION_CONFIRMED_BIT) !== 0,
  };
}

export function encodeInvestigation(state: InvestigationState): number {
  const interestCode = state.interest == null ? 0 : state.interest + 1;
  const confirmedCode = state.confirmedReviewer
    ? INVESTIGATION_CONFIRMED_BIT
    : 0;
  return interestCode + confirmedCode;
}

// O(n) linear scan, but arrays are bounded by the number of proposals/members
// in a round (typically <100), so this is fast in practice. Avoid using Maps
// here because the aggregate type is JSON-serialized.
function indexOfId(ids: string[], id: string): number {
  return ids.indexOf(id);
}

function ensureRowColumnSizes(round: RoundAggregate): void {
  const proposalCount = round.proposalMemberAxes.proposalIds.length;
  const memberCount = round.proposalMemberAxes.memberIds.length;

  while (round.investigation.length < proposalCount) {
    round.investigation.push(Array(memberCount).fill(0));
  }

  while (round.reviewProgress.length < proposalCount) {
    round.reviewProgress.push(Array(memberCount).fill(0));
  }

  for (const row of round.investigation) {
    while (row.length < memberCount) {
      row.push(0);
    }
  }

  for (const row of round.reviewProgress) {
    while (row.length < memberCount) {
      row.push(0);
    }
  }
}

export function ensureProposalInProposalMemberAxes(
  round: RoundAggregate,
  proposalId: string,
): number {
  let proposalIndex = indexOfId(
    round.proposalMemberAxes.proposalIds,
    proposalId,
  );

  if (proposalIndex === -1) {
    round.proposalMemberAxes.proposalIds.push(proposalId);
    proposalIndex = round.proposalMemberAxes.proposalIds.length - 1;
    ensureRowColumnSizes(round);
  }

  return proposalIndex;
}

export function ensureMemberInProposalMemberAxes(
  round: RoundAggregate,
  memberId: string,
): number {
  let memberIndex = indexOfId(round.proposalMemberAxes.memberIds, memberId);

  if (memberIndex === -1) {
    round.proposalMemberAxes.memberIds.push(memberId);
    memberIndex = round.proposalMemberAxes.memberIds.length - 1;
    ensureRowColumnSizes(round);
  }

  return memberIndex;
}

export function ensureProposalMemberIndex(
  round: RoundAggregate,
  proposalId: string,
  memberId: string,
): ProposalMemberIndex {
  const proposalIndex = ensureProposalInProposalMemberAxes(round, proposalId);
  const memberIndex = ensureMemberInProposalMemberAxes(round, memberId);
  return { proposalIndex, memberIndex };
}

export function getProposalMemberIndex(
  round: RoundAggregate,
  proposalId: string,
  memberId: string,
): ProposalMemberIndex | null {
  const proposalIndex = indexOfId(
    round.proposalMemberAxes.proposalIds,
    proposalId,
  );

  if (proposalIndex === -1) {
    return null;
  }

  const memberIndex = indexOfId(round.proposalMemberAxes.memberIds, memberId);

  if (memberIndex === -1) {
    return null;
  }

  return { proposalIndex, memberIndex };
}

export function getInvestigation(
  round: RoundAggregate,
  proposalId: string,
  memberId: string,
): InvestigationState | undefined {
  const index = getProposalMemberIndex(round, proposalId, memberId);

  if (index == null) {
    return undefined;
  }

  const code =
    round.investigation[index.proposalIndex]?.[index.memberIndex] ?? 0;

  if (code === 0) {
    return undefined;
  }

  return decodeInvestigation(code);
}

export function setInvestigation(
  round: RoundAggregate,
  proposalId: string,
  memberId: string,
  update: {
    interest?: number | null;
    confirmedReviewer?: boolean;
  },
): void {
  const { proposalIndex, memberIndex } = ensureProposalMemberIndex(
    round,
    proposalId,
    memberId,
  );
  const currentCode = round.investigation[proposalIndex]?.[memberIndex] ?? 0;
  const current = decodeInvestigation(currentCode);

  const next: InvestigationState = {
    interest:
      update.interest !== undefined
        ? update.interest === null
          ? undefined
          : update.interest
        : current.interest,
    confirmedReviewer:
      update.confirmedReviewer !== undefined
        ? update.confirmedReviewer
        : current.confirmedReviewer,
  };

  const investigationRow = (round.investigation[proposalIndex] ??= []);
  investigationRow[memberIndex] = encodeInvestigation(next);
}

export function getReviewProgress(
  round: RoundAggregate,
  proposalId: string,
  memberId: string,
): number | undefined {
  const index = getProposalMemberIndex(round, proposalId, memberId);

  if (index == null) {
    return undefined;
  }

  const progress =
    round.reviewProgress[index.proposalIndex]?.[index.memberIndex] ?? 0;
  return progress === 0 ? undefined : progress;
}

export function setReviewProgress(
  round: RoundAggregate,
  proposalId: string,
  memberId: string,
  progress: number | null,
): void {
  if (progress === null) {
    const existingIndex = getProposalMemberIndex(round, proposalId, memberId);

    if (existingIndex == null) {
      return;
    }

    const reviewProgressRow = (round.reviewProgress[
      existingIndex.proposalIndex
    ] ??= []);
    reviewProgressRow[existingIndex.memberIndex] = 0;
    return;
  }

  const { proposalIndex, memberIndex } = ensureProposalMemberIndex(
    round,
    proposalId,
    memberId,
  );
  // Valid progress values are 1-4 (enforced by the REVIEW_PROGRESS_CHANGE
  // action handler). 0 is reserved as the "not-set" sentinel in the matrix.
  const reviewProgressRow = (round.reviewProgress[proposalIndex] ??= []);
  reviewProgressRow[memberIndex] = progress;
}

export function getProposalDisclosure(
  round: RoundAggregate,
  proposalId: string,
  memberId: string,
): Disclosure | undefined {
  return round.proposalDisclosure[proposalId]?.[memberId];
}

export function getOrCreateProposalDisclosure(
  round: RoundAggregate,
  proposalId: string,
  memberId: string,
  createDisclosure: () => Disclosure,
): Disclosure {
  // Intentionally registers the (proposal, member) pair in the dense matrix
  // axes. Disclosures imply the member has a relationship with the proposal,
  // so the pair should be tracked in the axes for consistency.
  ensureProposalMemberIndex(round, proposalId, memberId);
  round.proposalDisclosure[proposalId] ??= {};

  if (!round.proposalDisclosure[proposalId][memberId]) {
    round.proposalDisclosure[proposalId][memberId] = createDisclosure();
  }

  return round.proposalDisclosure[proposalId][memberId];
}

export function getAllProposalDisclosureEntries(round: RoundAggregate): {
  proposalId: string;
  memberId: string;
  disclosure: Disclosure;
}[] {
  const entries: {
    proposalId: string;
    memberId: string;
    disclosure: Disclosure;
  }[] = [];

  for (const [proposalId, memberDisclosures] of Object.entries(
    round.proposalDisclosure,
  )) {
    for (const [memberId, disclosure] of Object.entries(memberDisclosures)) {
      entries.push({ proposalId, memberId, disclosure });
    }
  }

  return entries;
}
